<template>
  <c-loading-area :loading="origCourtCase=== null">
    <template v-if="origCourtCase!== null">
      <v-card-title>
        {{ $t('page.manageFiling.origCourt.edit.title') }}
      </v-card-title>
      <v-card-subtitle>
        {{ $t('page.manageFiling.origCourt.edit.description') }}
      </v-card-subtitle>
      <v-card-text class="pt-2">
        <!-- Case -->
        <v-divider class="my-2" style="opacity:0.5"/>
        <div class="d-flex">
          <div style="flex: 1 1 auto;align-self:center">
            <dl class="d-grid grid-4060 mt-1">
              <dt>{{ $t('page.manageFiling.origCourt.edit.case.section.title') }}</dt>
              <!-- Originating Court-->
              <dd>
                <div>
                  <div class="label text-left">
                    {{ $t('page.manageFiling.origCourt.edit.case.section.origCourt') }}
                  </div>
                  <div>
                    {{ origCourtCase.case.originatingCourt.courtName }}
                  </div>
                </div>
              </dd>
              <!-- Case Number -->
              <dd>
                <div>
                  <div class="label text-left">
                    {{ $t('page.manageFiling.origCourt.edit.case.section.caseNumber') }}
                  </div>
                  <div>
                    {{ origCourtCase.case.caseNumber }}
                  </div>
                </div>
              </dd>
              <!-- Case Title -->
              <template v-if="hasCaseTitle">
                <dd>
                  <div>
                    <div class="label text-left">
                      {{ $t('page.manageFiling.origCourt.edit.case.section.caseTitle') }}
                    </div>
                    <div>
                      {{ origCourtCase.case.caseTitle }}
                    </div>
                  </div>
                </dd>
              </template>
            </dl>
          </div>
          <div class="text-right" style="flex: 0 0 100px">
            <v-btn color="primary" text icon @click="$emit('editCase')">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </div>
        </div>
        <!-- Details -->
        <v-divider class="my-2" style="opacity:0.5"/>
        <div class="d-flex">
          <div style="flex: 1 1 auto;align-self:center">
            <dl class="d-grid grid-4060">
              <dt> {{ $t('page.manageFiling.origCourt.edit.details.section.title') }}</dt>
              <!-- Disposition Details -->
              <template v-if="hasDispositionType">
                <dd>
                  <div class="label text-left">
                    {{ $t('page.manageFiling.origCourt.edit.details.section.dispositionType') }}
                  </div>
                  <div>
                    {{ origCourtCase.details.dispositionType }}
                  </div>
                </dd>
              </template>
              <!-- Disposition Method -->
              <template v-if="hasDispositionMethod">
                <dd>
                  <div class="label text-left">
                    {{ $t('page.manageFiling.origCourt.edit.details.section.dispositionMethod') }}
                  </div>
                  <div>
                    {{ origCourtCase.details.dispositionMethod }}
                  </div>
                </dd>
              </template>
              <!-- Notice of Appeal Date -->
              <template v-if="hasNoticeOfAppealDate">
                <dd>
                  <div class="label text-left">
                    {{ $t('page.manageFiling.origCourt.edit.details.section.noticeOfAppealDate') }}
                  </div>
                  <div class="text-minimum-gray">
                    {{ $cove.formatDate(origCourtCase.details.noticeOfAppealDate) }}
                  </div>
                </dd>
              </template>
            </dl>
          </div>
          <div class="text-right" style="flex: 0 0 100px">
            <v-btn color="primary" text icon @click="$emit('editDetails')">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </div>
        </div>
        <!-- Judgments -->
        <v-divider class="my-2" style="opacity:0.5"/>
        <div class="d-flex">
          <div style="flex: 1 1 auto;align-self:center">
            <dl class="d-grid grid-4060">
              <dt>{{ $t('page.manageFiling.origCourt.edit.judgments.section.title') }}</dt>
              <template v-if="hasJudgments">
                <dd v-for="(judgment, index) in origCourtCase.judgments"
                    :key="`judgment-${index}`">
                  <div>
                    {{ generateJudgmentText(judgment.judge, getJudgmentTypeName(judgment)) }}
                  </div>
                  <div class="text-minimum-gray">
                    {{ $cove.formatDate(judgment.judgmentDate) }}
                  </div>
                </dd>
              </template>
            </dl>
          </div>
          <div class="text-right" style="flex: 0 0 100px">
            <v-btn color="primary" text icon @click="$emit('editJudgments')">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card-text>
      <v-card-actions class="justify-end mt-5">
        <c-btn @click="$emit('back')">
          {{ $t('page.manageFiling.origCourt.edit.backToOrigCourtCases') }}
        </c-btn>
      </v-card-actions>
    </template>
  </c-loading-area>
</template>

<script>
import _ from 'lodash';
import WorkflowStepMixin from '@/mixins/workflowStep';
import { generateJudgmentText } from '@/application/utility';

export default {
  mixins: [ WorkflowStepMixin ],
  props: {
    origCourtCase: {
      type: Object
    }
  },
  computed: {
    hasCaseTitle () {
      return !_.isNil(this.origCourtCase.case.caseTitle);
    },
    hasDispositionType () {
      return !_.isNil(this.origCourtCase.details.dispositionType);
    },
    hasDispositionMethod () {
      return !_.isNil(this.origCourtCase.details.dispositionMethod);
    },
    hasNoticeOfAppealDate () {
      return !_.isNil(this.origCourtCase.details.noticeOfAppealDate);
    },
    hasJudgments () {
      return !_.isEmpty(this.origCourtCase.judgments);
    }
  },
  methods: {
    generateJudgmentText,
    getJudgmentTypeName (judgment) {
      return _.get(judgment, 'judgmentType.judgmentTypeValue', null);
    }
  }
};
</script>

<style lang="scss" scoped>
.clamp-note {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
  overflow: hidden;
}

dl.d-grid dt
{
  font-weight: 600;
  text-align: left;
}
</style>